import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { SEARCH_DEBOUNCE_MS } from "@/ds";
import { DEFAULT_PROFISSIONAIS_SORT, listProfissionais } from "@/modules/recursos";
import type {
  ProfissionaisFilters,
  ProfissionaisPeriodo,
  ProfissionaisQueryState,
  ProfissionaisSort,
} from "@/modules/recursos";

/** O estado "sem permissão" da tela vem do `403` devolvido pela API. */
export const PROFISSIONAIS_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar os profissionais do Hub.";
export const PROFISSIONAIS_ERROR_MESSAGE = "Não foi possível carregar os profissionais.";

const INITIAL_STATE: ProfissionaisQueryState = {
  status: "loading",
  profissionais: [],
  total: 0,
  errorMessage: null,
};

export interface UseProfissionaisParams {
  search: string;
  filters: ProfissionaisFilters;
  /** Período Mês/Ano consultado; obrigatório em toda consulta (RN02). */
  periodo: ProfissionaisPeriodo;
  page: number;
  pageSize: number;
  sort?: ProfissionaisSort;
  /** Alocação a remanejar: a resposta marca quem já tem alocação nas datas dela. */
  alocacaoId?: string;
}

interface UseProfissionaisResult extends ProfissionaisQueryState {
  reload: () => void;
}

/** Model da listagem de Profissionais (consulta paginada no servidor); também alimenta o substituto da Carga de Trabalho. */
export function useProfissionais({
  search,
  filters,
  periodo,
  page,
  pageSize,
  sort = DEFAULT_PROFISSIONAIS_SORT,
  alocacaoId,
}: UseProfissionaisParams): UseProfissionaisResult {
  const [state, setState] = useState<ProfissionaisQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);

  const term = search.trim();
  const { cargo, time, indisponivel } = filters;
  const { field: sortField, direction: sortDirection } = sort;

  /**
   * Filtros e período são comparados pelo conteúdo: o efeito não pode depender
   * da identidade dos objetos, que muda a cada render do chamador.
   */
  const filtersKey = JSON.stringify([cargo, time, indisponivel]);
  const appliedFilters = useMemo(
    () => ({ cargo, time, indisponivel }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtersKey],
  );

  const periodoKey = JSON.stringify(periodo);
  const appliedPeriodo = useMemo(
    () => periodo,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodoKey],
  );

  useEffect(() => {
    // Debounce só na digitação; carga inicial, filtros, período, paginação e retry são imediatos.
    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    let cancelled = false;
    // Cancela a consulta anterior: paginar, filtrar ou trocar o período de novo descarta a resposta em trânsito.
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        listProfissionais({
          busca: term,
          filters: appliedFilters,
          periodo: appliedPeriodo,
          page,
          pageSize,
          sort: { field: sortField, direction: sortDirection },
          alocacaoId,
          signal: controller.signal,
        })
          .then(({ profissionais, total }) => {
            if (cancelled) return;
            setState({ status: "success", profissionais, total, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;

            const isForbidden = isAPIError(error) && error.status === 403;
            setState({
              status: isForbidden ? "forbidden" : "error",
              profissionais: [],
              total: 0,
              errorMessage: isForbidden
                ? PROFISSIONAIS_FORBIDDEN_MESSAGE
                : getErrorMessage(error) || PROFISSIONAIS_ERROR_MESSAGE,
            });
          });
      },
      shouldDebounce ? SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    term,
    appliedFilters,
    appliedPeriodo,
    page,
    pageSize,
    sortField,
    sortDirection,
    alocacaoId,
    reloadToken,
  ]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
